// Écran caisse (tablette du bar) : encaisser les tables et gérer les
// retraits à emporter du jour.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { staffFetch } from '../utils/staffApi';
import {
  StaffGate, useServiceSocket, useTicker, PayModal,
  ORDER_STATUS, euro, ago, timeHM
} from './staffCommon';
import { ProductPicker, TabDetailPanel, sendOrderToTab } from './TabComponents';

const formatPhone = (v) =>
  (v || '').replace(/\D/g, '').match(/.{1,2}/g)?.join(' ') || '';

function CashierBoard({ staff, onLogout }) {
  const [view, setView] = useState('tables'); // tables | pickup
  const [tables, setTables] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [openTab, setOpenTab] = useState(null);
  const [picker, setPicker] = useState(false);
  const [paying, setPaying] = useState(false);
  useTicker(30000);

  const openTabRef = useRef(null);
  openTabRef.current = openTab;

  const load = useCallback(async () => {
    try {
      const [ovRes, puRes] = await Promise.all([
        staffFetch('/service/overview', {}, onLogout),
        staffFetch('/service/pickup-today', {}, onLogout)
      ]);
      if (ovRes.ok) setTables((await ovRes.json()).tables);
      if (puRes.ok) setPickups(await puRes.json());
    } catch {
      // retenté au prochain événement
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const connected = useServiceSocket({
    'tab-updated': (tab) => {
      load();
      if (openTabRef.current && tab && tab.id === openTabRef.current.id) setOpenTab(tab);
    },
    'new-order': () => load(),
    'order-updated': () => load()
  });

  const showTab = async (tabId) => {
    const res = await staffFetch(`/service/tab/${tabId}`, {}, onLogout);
    if (res.ok) setOpenTab(await res.json());
  };

  const setPickupStatus = async (order, status) => {
    setPickups(prev => prev.map(o => o.id === order.id ? { ...o, status } : o));
    try {
      const res = await staffFetch(`/orders/${order.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      }, onLogout);
      if (!res.ok) load();
    } catch {
      load();
    }
  };

  const occupied = tables.filter(t => t.tab);
  const activePickups = pickups.filter(o => !['completed', 'cancelled'].includes(o.status));
  const donePickups = pickups.filter(o => ['completed', 'cancelled'].includes(o.status));
  const openTotal = occupied.reduce((s, t) => s + t.tab.total, 0);

  return (
    <div className="staff-screen">
      <div className="staff-topbar">
        <h1><span className={`staff-conn ${connected ? 'on' : ''}`} /> Caisse</h1>
        <div className="staff-chips">
          <button className={`staff-chip ${view === 'tables' ? 'on' : ''}`} onClick={() => setView('tables')}>
            Tables ({occupied.length})
          </button>
          <button className={`staff-chip ${view === 'pickup' ? 'on' : ''}`} onClick={() => setView('pickup')}>
            À emporter ({activePickups.length})
          </button>
        </div>
        <span className="staff-topbar-who">{staff && staff.name}</span>
        <button className="staff-logout" onClick={onLogout}>Quitter</button>
      </div>

      {view === 'tables' && (
        <div className="caisse-list">
          {occupied.length === 0 && (
            <div className="caisse-empty">Aucune table ouverte 🍺</div>
          )}
          {occupied.length > 0 && (
            <p style={{ color: '#9a948c', fontSize: 14 }}>
              {occupied.length} table{occupied.length > 1 ? 's' : ''} ouverte{occupied.length > 1 ? 's' : ''} — en attente d'encaissement : <strong style={{ color: '#C29A63' }}>{euro(openTotal)}</strong>
            </p>
          )}
          {occupied.map(t => (
            <button key={t.id} className="caisse-row" onClick={() => showTab(t.tab.id)}>
              <div className="caisse-row-main">
                <div className="caisse-row-title">
                  Table {t.name}
                  {t.tab.openedBy === 'qr' && <span className="st-pill st-pending">QR</span>}
                  {t.tab.inKitchen > 0 && <span className="st-pill st-preparing">⏳ {t.tab.inKitchen}</span>}
                  {t.tab.readyCount > 0 && <span className="st-pill st-ready">✅ {t.tab.readyCount}</span>}
                </div>
                <div className="caisse-row-sub">
                  {t.tab.ordersCount} envoi{t.tab.ordersCount > 1 ? 's' : ''}
                  {t.tab.lastOrderAt ? ` · dernier ${ago(t.tab.lastOrderAt)}` : ''}
                </div>
              </div>
              <span className="caisse-row-total">{euro(t.tab.total)}</span>
            </button>
          ))}
        </div>
      )}

      {view === 'pickup' && (
        <div className="caisse-list">
          {activePickups.length === 0 && (
            <div className="caisse-empty">Aucun retrait en attente aujourd'hui.</div>
          )}
          {activePickups.map(o => {
            const st = ORDER_STATUS[o.status];
            return (
              <div key={o.id} className="caisse-row" style={{ cursor: 'default' }}>
                <div className="caisse-row-main">
                  <div className="caisse-row-title">
                    {timeHM(o.pickupTime)} — {o.customerName}
                    <span className={`st-pill ${st.class}`}>{st.label}</span>
                  </div>
                  <div className="caisse-row-sub">
                    {(o.items || []).map(i => `${i.quantity}× ${i.name}`).join(' · ')}
                    {o.customerPhone ? ` — ${formatPhone(o.customerPhone)}` : ''}
                  </div>
                </div>
                <span className="caisse-row-total">{euro(o.total)}</span>
                {st.next && (
                  <button
                    className={`staff-btn ${o.status === 'ready' ? 'staff-btn-or' : 'staff-btn-dark'}`}
                    onClick={() => setPickupStatus(o, o.status === 'ready' ? 'completed' : st.next)}
                  >
                    {o.status === 'ready' ? 'Retiré & payé' : st.nextLabel}
                  </button>
                )}
              </div>
            );
          })}

          {donePickups.length > 0 && (
            <>
              <div className="kds-section-title" style={{ padding: '14px 2px 4px' }}>
                Terminés ({donePickups.length})
              </div>
              {donePickups.map(o => (
                <div key={o.id} className="caisse-row" style={{ opacity: 0.55, cursor: 'default' }}>
                  <div className="caisse-row-main">
                    <div className="caisse-row-title">{timeHM(o.pickupTime)} — {o.customerName}</div>
                  </div>
                  <span className="caisse-row-total">{euro(o.total)}</span>
                  <span className={`st-pill ${ORDER_STATUS[o.status].class}`}>{ORDER_STATUS[o.status].label}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {openTab && !picker && (
        <TabDetailPanel
          tab={openTab}
          tally
          onClose={() => setOpenTab(null)}
          onAddOrder={() => setPicker(true)}
          onPay={() => setPaying(true)}
          onCancel={async () => {
            const res = await staffFetch(`/service/tab/${openTab.id}/cancel`, { method: 'POST' }, onLogout);
            if (res.ok) { setOpenTab(null); load(); }
          }}
        />
      )}

      {openTab && picker && (
        <ProductPicker
          title={`Table ${openTab.tableName}`}
          onClose={() => setPicker(false)}
          onSend={async (items) => {
            const body = await sendOrderToTab(openTab.id, items);
            setOpenTab(body.tab);
            setPicker(false);
            load();
          }}
        />
      )}

      {openTab && paying && (
        <PayModal
          tab={openTab}
          onClose={() => setPaying(false)}
          onPaid={() => { setPaying(false); setOpenTab(null); load(); }}
        />
      )}
    </div>
  );
}

export default function CashierPage() {
  return (
    <StaffGate screenName="Caisse">
      {(staff, logout) => <CashierBoard staff={staff} onLogout={logout} />}
    </StaffGate>
  );
}
